// client/src/coms/views/canvas_parts/CanvasRightDock.js
import React from 'react';
import PaperSurfacePanel, { PaperIcon } from '../../canvas/paint-engine/integration/PaperSurfacePanel';
import { usePaperSurface } from '../../canvas/paint-engine/integration/paperSurfaceState';

function DockPanel({ title, children, grow = false, capped = false }) {
  return (
    <section
      style={{
        minHeight: 0,
        flex: grow ? '1 1 220px' : capped ? '0 0 min(45vh, 430px)' : '0 0 auto',
        borderBottom: '1px solid rgba(0,0,0,0.48)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          height: 27,
          flex: '0 0 27px',
          display: 'flex',
          alignItems: 'center',
          padding: '0 9px',
          boxSizing: 'border-box',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          background: 'rgba(255,255,255,0.045)',
          color: 'rgba(242,244,247,0.86)',
          fontSize: 12,
          fontWeight: 850,
        }}
      >
        {title}
      </div>
      <div style={{ padding: 8, minHeight: 0, overflow: grow || capped ? 'auto' : 'visible', boxSizing: 'border-box' }}>
        {children}
      </div>
    </section>
  );
}

export default function CanvasRightDock({
  strokeColor,
  setStrokeColor,
  layersPanel,
  animationPanel,
  animationEnabled,
  sidebarExtensionPanel,
  mobileView = false,
  mobileOpen = false,
}) {
  const safeStrokeColor = /^#[0-9a-f]{6}$/i.test(String(strokeColor || '')) ? strokeColor : '#000000';
  const [activeTab, setActiveTab] = React.useState('layers');

  React.useEffect(() => {
    setActiveTab(animationEnabled ? 'animation' : 'layers');
  }, [animationEnabled]);

  React.useEffect(() => {
    if (!animationPanel && activeTab === 'animation') setActiveTab('layers');
  }, [activeTab, animationPanel]);

  // Paper & Surface is a document property: its tab is always available
  // (whatever tool is selected) when the physical paint engine is running.
  const { available: paperAvailable } = usePaperSurface();
  // File > Paper & Surface > Surface Settings… opens this tab.
  const [paperRequest, setPaperRequest] = React.useState(null);
  const paperPanelRef = React.useRef(null);
  React.useEffect(() => {
    const openPaper = (event) => {
      setActiveTab('paper');
      setPaperRequest({ advanced: Boolean(event && event.detail && event.detail.advanced), at: Date.now() });
    };
    window.addEventListener('vx-open-paper-settings', openPaper);
    return () => window.removeEventListener('vx-open-paper-settings', openPaper);
  }, []);
  React.useEffect(() => {
    if (!paperRequest || activeTab !== 'paper' || !paperPanelRef.current) return undefined;
    if (typeof paperPanelRef.current.scrollIntoView === 'function') paperPanelRef.current.scrollIntoView({ block: 'nearest' });
    const timer = window.setTimeout(() => setPaperRequest((current) => (current && current.at === paperRequest.at ? { ...current, highlight: false } : current)), 1400);
    return () => window.clearTimeout(timer);
  }, [activeTab, paperRequest]);
  const tabCount = 1 + (animationPanel ? 1 : 0) + (paperAvailable ? 1 : 0);

  return (
    <aside
      aria-label="Canvas right dock"
      style={{
        width: mobileView ? '50vw' : 312,
        maxWidth: mobileView ? '50vw' : 312,
        flex: mobileView ? '0 0 50vw' : '0 0 312px',
        position: mobileView ? 'absolute' : 'relative',
        top: mobileView ? 0 : 'auto',
        right: mobileView ? 0 : 'auto',
        bottom: mobileView ? 0 : 'auto',
        zIndex: mobileView ? 40 : 'auto',
        transform: mobileView
          ? (mobileOpen ? 'translate3d(0,0,0)' : 'translate3d(100%,0,0)')
          : 'none',
        transition: mobileView ? 'transform 180ms ease-out' : 'none',
        pointerEvents: mobileView && !mobileOpen ? 'none' : 'auto',
        borderLeft: '1px solid rgba(0,0,0,0.55)',
        background: '#3b3b3b',
        color: 'rgba(245,247,250,0.94)',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
        boxShadow: mobileView && mobileOpen ? '-8px 0 24px rgba(0,0,0,0.34)' : 'none',
      }}
    >
      <DockPanel title="Advanced Colour Selector">
        <div style={{ display: 'grid', gridTemplateColumns: '54px minmax(0, 1fr)', gap: 10, alignItems: 'center' }}>
          <input
            type="color"
            value={safeStrokeColor}
            onChange={(event) => setStrokeColor && setStrokeColor(event.target.value)}
            aria-label="Stroke colour"
            style={{
              width: 52,
              height: 42,
              border: '1px solid rgba(255,255,255,0.14)',
              borderRadius: 5,
              background: 'transparent',
              padding: 0,
              cursor: 'pointer',
            }}
          />
          <input
            type="text"
            value={strokeColor || '#000000'}
            onChange={(event) => setStrokeColor && setStrokeColor(event.target.value)}
            style={{
              width: '100%',
              height: 30,
              borderRadius: 4,
              border: '1px solid rgba(255,255,255,0.12)',
              background: 'rgba(0,0,0,0.22)',
              color: 'rgba(255,255,255,0.92)',
              padding: '0 8px',
              boxSizing: 'border-box',
              fontSize: 12,
              fontWeight: 800,
            }}
          />
        </div>
      </DockPanel>

      <DockPanel title={tabCount > 1 ? 'Canvas' : 'Layers'} grow>
        {tabCount > 1 ? (
          <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${tabCount}, 1fr)`, gap: 6, marginBottom: 8 }}>
              <button
                type="button"
                onClick={() => setActiveTab('layers')}
                style={{
                  minHeight: 30,
                  borderRadius: 7,
                  border: activeTab === 'layers' ? '1px solid rgba(96,165,250,0.70)' : '1px solid rgba(255,255,255,0.10)',
                  background: activeTab === 'layers' ? 'rgba(59,130,246,0.22)' : 'rgba(255,255,255,0.055)',
                  color: 'rgba(245,247,250,0.94)',
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: 'pointer',
                }}
              >
                Layers
              </button>
              {animationPanel ? (
              <button
                type="button"
                onClick={() => setActiveTab('animation')}
                style={{
                  minHeight: 30,
                  borderRadius: 7,
                  border: activeTab === 'animation' ? '1px solid rgba(96,165,250,0.70)' : '1px solid rgba(255,255,255,0.10)',
                  background: activeTab === 'animation' ? 'rgba(59,130,246,0.22)' : animationEnabled ? 'rgba(255,255,255,0.070)' : 'rgba(255,255,255,0.045)',
                  color: animationEnabled ? 'rgba(245,247,250,0.96)' : 'rgba(245,247,250,0.70)',
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: 'pointer',
                }}
              >
                Animation
              </button>
              ) : null}
              {paperAvailable ? (
              <button
                type="button"
                onClick={() => setActiveTab('paper')}
                aria-pressed={activeTab === 'paper'}
                style={{
                  minHeight: 30,
                  borderRadius: 7,
                  border: activeTab === 'paper' ? '1px solid rgba(96,165,250,0.70)' : '1px solid rgba(255,255,255,0.10)',
                  background: activeTab === 'paper' ? 'rgba(59,130,246,0.22)' : 'rgba(255,255,255,0.055)',
                  color: 'rgba(245,247,250,0.94)',
                  fontSize: 12,
                  fontWeight: 900,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}><PaperIcon />Paper</span>
              </button>
              ) : null}
            </div>
            <div style={{ minHeight: 0, flex: '1 1 auto', overflow: 'auto' }}>
              {activeTab === 'paper' && paperAvailable
                ? (
                  <div
                    ref={paperPanelRef}
                    data-testid="paper-surface-settings"
                    style={{
                      borderRadius: 8,
                      transition: 'box-shadow 400ms ease-out',
                      boxShadow: paperRequest && paperRequest.highlight !== false ? '0 0 0 2px rgba(96,165,250,0.75)' : '0 0 0 0 rgba(96,165,250,0)',
                    }}
                  >
                    <PaperSurfacePanel available={paperAvailable} advancedRequest={paperRequest && paperRequest.advanced ? paperRequest.at : 0} />
                  </div>
                )
                : activeTab === 'animation' && animationPanel ? animationPanel : layersPanel}
            </div>
          </div>
        ) : layersPanel}
      </DockPanel>

      {sidebarExtensionPanel ? (
        <DockPanel title="Tool Settings" capped>
          {sidebarExtensionPanel}
        </DockPanel>
      ) : null}
    </aside>
  );
}
